/**
 * Supabase Client Configuration
 *
 * This module initializes and exports the Supabase client for database operations,
 * authentication, and storage interactions with comprehensive security features.
 *
 * Security Features:
 * - Automatic token refresh
 * - Secure session persistence
 * - Request retry with exponential backoff
 * - Error sanitization to prevent information leakage
 * - PKCE flow for authentication
 * - Session timeout detection
 *
 * @module services/supabase
 */

import { createClient } from '@supabase/supabase-js';
import { resolveSupabaseConfig } from './supabaseConfig';

// Security: Validate configuration at startup.
//
// La guarda (presencia de las variables + `https://` salvo loopback fuera de
// producción) vive en `./supabaseConfig`, que es un módulo puro y testeable.
// Ver ahí el porqué del recorte por HOST + no-producción (#698).
//
// Si esto tira, el throw ocurre al importar el módulo — React nunca monta y el
// ErrorBoundary no puede verlo. Por eso `public/index.html` registra un
// `window.addEventListener('error')` ANTES del bundle, que reemplaza el
// placeholder `#app-loading` por una pantalla que dice qué variable falló.
// Sin eso, el único síntoma era "Cargando ProMedic..." para siempre (#698).
const { url: supabaseUrl, anonKey: supabaseAnonKey } = resolveSupabaseConfig(
  process.env
);

/**
 * Supabase client instance
 * Configured with security best practices:
 * - PKCE flow for enhanced security
 * - Automatic token refresh (prevents session expiry)
 * - Secure session persistence (httpOnly would be ideal but not supported in SPA)
 * - Flow type: PKCE (Proof Key for Code Exchange) for OAuth security
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // SECURITY: Enable automatic token refresh to maintain session
    autoRefreshToken: true,
    // SECURITY: Persist session securely in localStorage (SPA limitation)
    persistSession: true,
    // SECURITY: Detect session from URL (for OAuth callbacks)
    detectSessionInUrl: true,
    // SECURITY: Use localStorage (httpOnly cookies not available in SPA)
    storage: window.localStorage,
    // SECURITY: Use PKCE flow for enhanced OAuth security
    flowType: 'pkce',
    // SECURITY: Debug mode disabled in production
    debug: process.env.REACT_APP_ENV === 'development',
  },
  global: {
    headers: {
      // SECURITY: Add custom headers for tracking
      'x-application-name': 'ProMedic-PMS',
    },
  },
  db: {
    // SECURITY: Schema validation
    schema: 'public',
  },
  // SECURITY: Retry configuration with exponential backoff
  // This prevents overwhelming the server during failures
  realtime: {
    // Disable realtime if not needed (reduces attack surface)
    enabled: false,
  },
});

/**
 * Sanitize error messages to prevent information leakage
 * SECURITY: Never expose internal details, stack traces, or database errors to users
 * @param {Error} error - Original error
 * @returns {Error} Sanitized error safe for user display
 */
const sanitizeError = (error) => {
  // SECURITY: Map specific errors to generic user-friendly messages
  const errorMap = {
    'Invalid login credentials': 'Correo o contraseña incorrectos',
    'Email not confirmed': 'Correo no confirmado. Revisa tu bandeja de entrada',
    'User not found': 'Correo o contraseña incorrectos', // Same message as invalid credentials
    'Invalid email': 'Formato de correo inválido',
    'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
    'Too many requests': 'Demasiados intentos. Espera unos minutos',
    'Network request failed': 'Error de red. Verifica tu conexión',
  };

  const message = error?.message || 'An error occurred';
  // Check for partial matches (e.g. Supabase internal DB errors)
  let sanitizedMessage = errorMap[message];
  if (!sanitizedMessage && message.includes('Database error querying schema')) {
    sanitizedMessage = 'Error temporal del servidor. Por favor intenta de nuevo.';
  }
  sanitizedMessage = sanitizedMessage || 'Ocurrió un error. Por favor intenta de nuevo';

  // SECURITY: Create new error with sanitized message (no stack trace exposure)
  const sanitizedError = new Error(sanitizedMessage);
  sanitizedError.code = error?.code || 'UNKNOWN_ERROR';

  return sanitizedError;
};

/**
 * Rate limiting tracker for login attempts
 * SECURITY: Prevents brute force attacks by tracking failed attempts
 */
const loginAttempts = {
  attempts: {},

  /**
   * Check if email has exceeded rate limit
   * @param {string} email - User email
   * @returns {boolean} True if rate limited
   */
  isRateLimited(email) {
    const key = email.toLowerCase();
    const now = Date.now();
    const attempts = this.attempts[key] || [];

    // SECURITY: Clean up old attempts (older than 15 minutes)
    const recentAttempts = attempts.filter(time => now - time < 15 * 60 * 1000);
    this.attempts[key] = recentAttempts;

    // SECURITY: Rate limit: Max 5 attempts per 15 minutes
    return recentAttempts.length >= 5;
  },

  /**
   * Record failed login attempt
   * @param {string} email - User email
   */
  recordAttempt(email) {
    const key = email.toLowerCase();
    this.attempts[key] = this.attempts[key] || [];
    this.attempts[key].push(Date.now());
  },

  /**
   * Clear attempts on successful login
   * @param {string} email - User email
   */
  clearAttempts(email) {
    const key = email.toLowerCase();
    delete this.attempts[key];
  }
};

/**
 * Authentication service methods
 * All methods implement security best practices:
 * - Error sanitization
 * - Rate limiting
 * - Input validation
 * - Secure session management
 */
export const authService = {
  /**
   * Sign in with email and password
   * SECURITY: Implements rate limiting and error sanitization
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{user, session, error}>}
   */
  async signIn(email, password) {
    try {
      // SECURITY: Input validation
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // SECURITY: Rate limiting check
      if (loginAttempts.isRateLimited(email)) {
        throw new Error('Too many requests');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(), // SECURITY: Normalize email
        password,
      });

      if (error) {
        // SECURITY: Record failed attempt
        loginAttempts.recordAttempt(email);
        throw error;
      }

      // SECURITY: Clear attempts on successful login
      loginAttempts.clearAttempts(email);

      return { user: data.user, session: data.session, error: null };
    } catch (error) {
      // SECURITY: Log original error internally (for debugging)
      console.error('[AUTH] Sign in error:', error.message);
      // SECURITY: Return sanitized error to caller
      return { user: null, session: null, error: sanitizeError(error) };
    }
  },

  /**
   * Sign out current user
   *
   * `scope: 'local'` y no el default (`global`), a propósito. El default revoca
   * TODAS las sesiones de la persona en el proyecto Supabase, no la del
   * navegador que apretó "salir" — así que un logout en la web desconectaba
   * también el connector de ProMedic en ChatGPT (el botón "Reconnect" que
   * aparecía varias veces por día) y cualquier otra pestaña o dispositivo del
   * mismo usuario.
   *
   * Lo que el usuario pidió fue cerrar ESTA sesión. Cerrar las demás es una
   * operación distinta, y la que de verdad necesita cerrarlas todas
   * —desactivar una cuenta, resetear una contraseña— ya lo hace del lado del
   * servidor con `admin.signOut(id, 'global')` y revocando las concesiones
   * OAuth (ver `mcp-server/src/tools/userTools.js`).
   *
   * El único lugar que pide `global` lo pide EXPLÍCITO: el botón "Cerrar todas
   * las sesiones" del perfil, que es lo que promete hacer.
   *
   * @param {Object} [opciones]
   * @param {'local'|'global'} [opciones.scope]  Default `local`.
   * @returns {Promise<{error}>}
   */
  async signOut({ scope = 'local' } = {}) {
    try {
      const { error } = await supabase.auth.signOut({ scope });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  },

  /**
   * Get current session
   * @returns {Promise<{session, error}>}
   */
  async getSession() {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return { session: data.session, error: null };
    } catch (error) {
      console.error('Get session error:', error);
      return { session: null, error };
    }
  },

  /**
   * Get current user
   * @returns {Promise<{user, error}>}
   */
  async getCurrentUser() {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { user: data.user, error: null };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error };
    }
  },

  /**
   * Refresh session
   * @returns {Promise<{session, error}>}
   */
  async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return { session: data.session, error: null };
    } catch (error) {
      console.error('Refresh session error:', error);
      return { session: null, error };
    }
  },

  /**
   * Get user profile with role information
   * @param {string} userId - User ID
   * @returns {Promise<{profile, error}>}
   */
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return { profile: data, error: null };
    } catch (error) {
      console.error('Get user profile error:', error);
      return { profile: null, error };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object} updates - Profile updates
   * @returns {Promise<{profile, error}>}
   */
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { profile: data, error: null };
    } catch (error) {
      console.error('Update user profile error:', error);
      return { profile: null, error };
    }
  },

  /**
   * Request password reset
   * SECURITY: Implements rate limiting to prevent abuse
   * @param {string} email - User email
   * @returns {Promise<{success: boolean, error}>}
   */
  async requestPasswordReset(email) {
    try {
      // SECURITY: Input validation
      if (!email) {
        throw new Error('Email is required');
      }

      // SECURITY: Rate limiting check
      if (loginAttempts.isRateLimited(email)) {
        throw new Error('Too many requests');
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        {
          redirectTo: `${window.location.origin}/reset-password`,
        }
      );

      if (error) {
        loginAttempts.recordAttempt(email);
        throw error;
      }

      // SECURITY: Always return success (don't reveal if email exists)
      return { success: true, error: null };
    } catch (error) {
      console.error('[AUTH] Password reset error:', error.message);
      // SECURITY: Generic success message even on error (prevent email enumeration)
      return { success: true, error: null };
    }
  },

  /**
   * Update password
   * SECURITY: Requires authenticated session
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, error}>}
   */
  async updatePassword(newPassword) {
    try {
      // SECURITY: Password strength validation
      if (!newPassword || newPassword.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error('[AUTH] Update password error:', error.message);
      return { success: false, error: sanitizeError(error) };
    }
  },

  /**
   * Subscribe to authentication state changes
   * @param {Function} callback - Callback function to handle auth state changes
   * @returns {Object} Subscription object
   */
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange((event, session) => {
      // SECURITY: Log auth events for security monitoring
      console.log('[AUTH] Event:', event);
      callback(event, session);
    });
  },

  /**
   * Check session validity
   * SECURITY: Verify session hasn't expired and refresh if needed
   * @returns {Promise<{valid: boolean, session, error}>}
   */
  async checkSessionValidity() {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) throw error;

      if (!data.session) {
        return { valid: false, session: null, error: null };
      }

      // SECURITY: Check if token is about to expire (within 5 minutes)
      const expiresAt = data.session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry < 300) { // 5 minutes
        // SECURITY: Proactively refresh token
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) throw refreshError;

        return { valid: true, session: refreshData.session, error: null };
      }

      return { valid: true, session: data.session, error: null };
    } catch (error) {
      console.error('[AUTH] Session validity check error:', error.message);
      return { valid: false, session: null, error: sanitizeError(error) };
    }
  },
};

/**
 * Storage service methods
 * Implements secure file upload with validation
 */
export const storageService = {
  /**
   * Get signed URL for file download
   * SECURITY: URLs expire after 1 hour
   * @param {string} bucket - Bucket name
   * @param {string} path - File path
   * @returns {Promise<{url, error}>}
   */
  async getSignedUrl(bucket, path) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 3600); // SECURITY: 1 hour expiry

      if (error) throw error;

      return { url: data.signedUrl, error: null };
    } catch (error) {
      console.error('[STORAGE] Get signed URL error:', error.message);
      return { url: null, error: sanitizeError(error) };
    }
  },

  /**
   * Upload file to storage
   * SECURITY: File validation handled by fileUpload utility
   * @param {string} bucket - Bucket name
   * @param {string} path - File path
   * @param {File} file - File to upload
   * @returns {Promise<{path, error}>}
   */
  async uploadFile(bucket, path, file) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false, // SECURITY: Don't overwrite existing files
        });

      if (error) throw error;

      return { path: data.path, error: null };
    } catch (error) {
      console.error('[STORAGE] Upload error:', error.message);
      return { path: null, error: sanitizeError(error) };
    }
  },

  /**
   * Delete file from storage
   * SECURITY: RLS policies enforce ownership
   * @param {string} bucket - Bucket name
   * @param {string} path - File path
   * @returns {Promise<{success: boolean, error}>}
   */
  async deleteFile(bucket, path) {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error('[STORAGE] Delete error:', error.message);
      return { success: false, error: sanitizeError(error) };
    }
  },

  /**
   * List files in bucket
   * SECURITY: RLS policies enforce visibility
   * @param {string} bucket - Bucket name
   * @param {string} folder - Folder path (optional)
   * @returns {Promise<{files, error}>}
   */
  async listFiles(bucket, folder = '') {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(folder);

      if (error) throw error;

      return { files: data, error: null };
    } catch (error) {
      console.error('[STORAGE] List files error:', error.message);
      return { files: [], error: sanitizeError(error) };
    }
  },
};

export default supabase;
